import type { ReactNode } from "react";

/**
 * Renders text where *asterisks* mark the accent phrase.
 *
 * The design italicises one phrase in each headline in the clay colour - "and
 * *renewal*", "Small room, *full attention*". Once headlines became editable
 * that had to be expressible in a plain text field, and a single asterisk pair
 * is the least a person has to learn.
 *
 * Deliberately not a Markdown parser and deliberately not dangerouslySetInnerHTML:
 * everything between the asterisks is rendered as a React text node, so copy
 * typed into the admin page cannot inject markup or script.
 */
export function Accented({ text }: { text: string }): ReactNode {
  if (!text.includes("*")) return text;

  const parts: ReactNode[] = [];
  // Split on the asterisk pairs, keeping the captured inner text so the odd
  // indices are the accented segments.
  const segments = text.split(/\*([^*]+)\*/g);

  segments.forEach((segment, index) => {
    if (segment === "") return;
    if (index % 2 === 1) {
      parts.push(<em key={index}>{segment}</em>);
    } else {
      parts.push(segment);
    }
  });

  return parts;
}

/**
 * The same text with the markers stripped, for places that cannot hold markup -
 * a document title, a meta description, an aria-label.
 */
export function plainText(text: string): string {
  return text.replace(/\*([^*]+)\*/g, "$1");
}
